import React from 'react';
import { Shield, Lock, Eye, FileText } from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-paper">
      <Navigation />
      
      <main className="max-w-4xl mx-auto px-6 py-24">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-panel rounded-none mb-6">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-ink mb-4">
            Privacy Policy
          </h1>
          <p className="text-muted text-lg">
            Last updated: September 27, 2025
          </p>
        </div>

        {/* Content */}
        <div className="space-y-12">
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-surface rounded-none flex items-center justify-center">
                <Lock className="h-6 w-6 text-ink" />
              </div>
              <h2 className="text-2xl font-bold text-ink">
                1. Information We Collect
              </h2>
            </div>
            <p className="text-muted leading-relaxed mb-4">
              When you use DressUp, we may collect the following types of information:
            </p>
            <ul className="space-y-3 text-muted">
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                <div>
                  <strong className="text-ink">Personal Information:</strong> Name, email address, and account details you provide during sign-up.
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                <div>
                  <strong className="text-ink">Photo Data:</strong> Images you upload for computer vision analysis to identify your features and personalize recommendations.
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                <div>
                  <strong className="text-ink">Physical Profile:</strong> Body type, height, age range, hair color, weight range.
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                <div>
                  <strong className="text-ink">Style Preferences:</strong> Trend-following nature, fashion risk tolerance, fit preferences, budget, primary and secondary styles.
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                <div>
                  <strong className="text-ink">Device & Usage Data:</strong> IP address, device type, operating system, browser type, app usage data, and interaction patterns.
                </div>
              </li>
            </ul>
          </section>

          <div className="border-t border-line" />

          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-surface rounded-none flex items-center justify-center">
                <Eye className="h-6 w-6 text-ink" />
              </div>
              <h2 className="text-2xl font-bold text-ink">
                2. How We Use Your Information
              </h2>
            </div>
            <p className="text-muted leading-relaxed mb-4">
              We use the information we collect to:
            </p>
            <ul className="space-y-3 text-muted">
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                Provide, maintain, and improve the Service.
              </li>
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                Create a hyper-personalized fashion marketplace tailored to your profile.
              </li>
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                Improve our AI algorithms and recommendation quality.
              </li>
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                Communicate with you about updates, new features, and promotions.
              </li>
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                Detect, prevent, and address fraud, abuse, or technical issues.
              </li>
            </ul>
          </section>

          <div className="border-t border-line" />

          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-surface rounded-none flex items-center justify-center">
                <FileText className="h-6 w-6 text-ink" />
              </div>
              <h2 className="text-2xl font-bold text-ink">
                3. Sharing Your Information
              </h2>
            </div>
            <p className="text-muted leading-relaxed mb-4">
              We do not sell, rent, or share your personal information with fashion brands or third parties for marketing purposes. Brands only provide their product catalogs to DressUp, and we use our personalization technology to match those products to your profile.
            </p>
            <p className="text-muted leading-relaxed mb-4">
              We may share limited information with:
            </p>
            <ul className="space-y-3 text-muted">
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                <div>
                  <strong className="text-ink">Service Providers:</strong> Third parties that perform services on our behalf (e.g., cloud hosting, analytics, AI infrastructure).
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                <div>
                  <strong className="text-ink">Legal Authorities:</strong> When required to comply with applicable law or protect our rights.
                </div>
              </li>
            </ul>
          </section>

          <div className="border-t border-line" />

          <section>
            <h2 className="text-2xl font-bold text-ink mb-4">
              4. Your Choices
            </h2>
            <p className="text-muted leading-relaxed mb-4">
              You can control your information through:
            </p>
            <ul className="space-y-3 text-muted">
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                <div>
                  <strong className="text-ink">Account Settings:</strong> Update your profile and preferences at any time.
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                <div>
                  <strong className="text-ink">Marketing Communications:</strong> Opt-out of marketing emails using the unsubscribe link.
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                <div>
                  <strong className="text-ink">Data Requests:</strong> Request access to, correction, or deletion of your personal information.
                </div>
              </li>
            </ul>
          </section>

          <div className="border-t border-line" />

          <section>
            <h2 className="text-2xl font-bold text-ink mb-4">
              5. Security
            </h2>
            <p className="text-muted leading-relaxed">
              We implement appropriate technical and organizational measures to protect your information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <div className="border-t border-line" />

          <section>
            <h2 className="text-2xl font-bold text-ink mb-4">
              6. Contact Us
            </h2>
            <p className="text-muted leading-relaxed mb-4">
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <div className="bg-surface border-l-4 border-accent p-6 rounded-none">
              <p className="text-muted mb-2">
                <strong className="text-ink">Email:</strong> abdulbasit@thedressup.co
              </p>
              <p className="text-muted">
                <strong className="text-ink">Address:</strong> NITB Building, National Incubation Center, 24-B Street 6, Sector H-9/1, Islamabad, 44000, Pakistan
              </p>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
